<script setup lang="ts">
import { computed, ref, watch, type Ref } from 'vue'
import { storeToRefs } from 'pinia'
import GbDialog from '@/components/GbDialog.vue'
import GbButton from '@/components/GbButton.vue'
import GbInlineMessage from '@/components/GbInlineMessage.vue'
import InputText from 'primevue/inputtext'
import ProgressSpinner from 'primevue/progressspinner'
import { useLibraryStore } from '@/library/library.store'
import { useUserStore } from '@/authentication/user.store'
import { updateCollectionContent } from '@/library/api'
import { normalizeUrl, validateURL, isDuplicateUrl, getLinkType, ATTACHMENT_TYPE } from '@/library/utils/urlUtils'
import { CONTENT_TYPES } from '@/util/access_visibility'
import { useLibraryFolderAnalytics } from '@/library/composables/useLibraryFolderAnalytics'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin'
import { EVENT_NAMES } from '@/tracking/analytics/event_names'

const props = withDefaults(
  defineProps<{
    externalOpen: boolean
    folderUuid: string
    folderName?: string
    contentUuid?: string
    initialUrl?: string
    initialTitle?: string
  }>(),
  {
    folderName: ''
  }
)

const emit = defineEmits<{
  'update:externalOpen': [value: boolean]
  saved: []
}>()

const libraryStore = useLibraryStore()
const userStore = useUserStore()
const { userId } = storeToRefs(userStore)
const { attachments } = storeToRefs(libraryStore)

const { getReactiveEventAttributes } = useLibraryFolderAnalytics()
const analytics = getAnalyticsTracker().getChildContext(getReactiveEventAttributes())

const visibleRef: Ref<boolean> = ref(props.externalOpen)

const url = ref('')
const title = ref('')
const errorMessage = ref('')
const isSubmitting = ref(false)

const isEditMode = computed(() => !!props.contentUuid)

const dialogId = computed(() => `attachment-modal-${props.folderUuid || 'unknown'}`)

watch(
  () => props.externalOpen,
  (newVal) => {
    visibleRef.value = newVal
    if (newVal) {
      url.value = props.initialUrl || ''
      title.value = props.initialTitle || ''
      errorMessage.value = ''
      isSubmitting.value = false
      
      if (!isEditMode.value) {
        analytics.sendEvent(EVENT_NAMES.LIBRARY.ATTACHMENT.OPENED_ADD_ATTACHMENT_MODAL, {
          attachment_type: ATTACHMENT_TYPE.LINK
        })
      }
    }
  }
)

watch(
  () => visibleRef.value,
  (newVal) => {
    emit('update:externalOpen', newVal)
  }
)

async function submit() {
  errorMessage.value = ''
  const validationError = validateURL(url.value)
  if (validationError) {
    errorMessage.value = validationError
    return
  }

  const normalizedUrl = normalizeUrl(url.value)
  if (!normalizedUrl) {
    errorMessage.value = 'Enter a valid URL'
    return
  }

  const isDuplicate = attachments.value.some((attachment) => {
    const attachmentUrl = (attachment.content_body as any)?.url
    if (!attachmentUrl) return false
    if (isEditMode.value && attachment.content_uuid === props.contentUuid) {
      return false
    }
    return isDuplicateUrl(normalizedUrl, attachmentUrl)
  })

  if (isDuplicate) {
    errorMessage.value = '<strong>Duplicate link:</strong> This link as already been added. Please add a new link.'
    return
  }

  const trimmedTitle = title.value.trim()
  const contentTitle = isEditMode.value
    ? trimmedTitle
    : (trimmedTitle === '' ? undefined : trimmedTitle)

  isSubmitting.value = true
  try {
    const linkType = getLinkType(normalizedUrl)
    const attachmentType = ATTACHMENT_TYPE.LINK
    
    if (isEditMode.value && props.contentUuid) {
      await updateCollectionContent({
        collectionUuid: props.folderUuid,
        contentUuid: props.contentUuid,
        url: normalizedUrl,
        contentTitle,
        userUuid: userId.value
      })
      
      const urlChanged = normalizedUrl !== normalizeUrl(props.initialUrl || '')
      const titleChanged = title.value.trim() !== (props.initialTitle || '').trim()
      
      analytics.sendEvent(EVENT_NAMES.LIBRARY.ATTACHMENT.SUBMITTED_ATTACHMENT_EDIT, {
        is_edited_url: urlChanged,
        is_edited_title: titleChanged,
        attachment_type: attachmentType,
        attachment_uuid: props.contentUuid
      })
    } else {
      const newContentUuid = crypto.randomUUID()
      await libraryStore.saveToLibrary({
        contentType: CONTENT_TYPES.ATTACHMENT_LINK,
        contentUuid: newContentUuid,
        collectionUuid: props.folderUuid,
        contributor: userId.value,
        contentPayload: {
          url: normalizedUrl,
          contentTitle
        }
      })
      
      analytics.sendEvent(EVENT_NAMES.LIBRARY.ATTACHMENT.CLICKED_ADD_ATTACHMENT, {
        link_type: linkType,
        attachment_uuid: newContentUuid,
        link_title_provided: !!trimmedTitle,
        attachment_type: attachmentType
      })
    }
    emit('saved')
    visibleRef.value = false
  } catch {
    errorMessage.value = isEditMode.value
      ? 'Failed to update attachment. Please try again.'
      : 'Failed to add attachment. Please try again.'
  } finally {
    isSubmitting.value = false
  }
}

function cancel() {
  visibleRef.value = false
}
</script>

<template>
  <GbDialog
    :dialogId="dialogId"
    v-model:visible="visibleRef"
    :show-header-content="true"
    :footer-buttons="true"
    :show-close-icon="true"
    @hide="cancel"
  >
    <template #headerContent>
      <div class="flex items-center gap-2 min-w-0">
        <span class="text-xl font-normal whitespace-nowrap">{{ isEditMode ? 'Edit Link in' : 'Add Link to' }}</span>
        <span class="material-icons-round text-gray-700 shrink-0">folder</span>
        <span class="text-xl font-bold truncate min-w-0">{{ props.folderName }}</span>
      </div>
    </template>

    <template #bodyContent>
      <div class="flex flex-col gap-6">
        <div class="flex flex-col gap-2">
          <label :for="`${dialogId}-link-url-input`" class="text-sm font-medium">URL</label>
          <InputText :id="`${dialogId}-link-url-input`" v-model="url" placeholder="https://example.com" />
          <div v-if="errorMessage" class="flex items-start gap-1 text-sm font-medium leading-[18px]" style="color: var(--text-toolkit-danger-default, #A94442); font-family: 'Helvetica Neue'; font-weight: 500;">
            <span class="material-icons-outlined text-base mt-px">warning_amber</span>
            <span v-html="errorMessage"></span>
          </div>
        </div>

        <div class="flex flex-col gap-2">
          <label :for="`${dialogId}-link-title-input`" class="text-sm font-medium">Title (optional)</label>
          <InputText :id="`${dialogId}-link-title-input`" v-model="title" placeholder="Enter a descriptive title" />
        </div>

        <GbInlineMessage gbTheme="primary">
          <template #icon>
            <span class="material-icons-outlined text-blue-400">info</span>
          </template>
          <template #default>
            <span class="font-bold">Note:</span>
            Not everyone may be able to access this link. Access is determined by the source file’s share settings (e.g., Google Drive).
          </template>
        </GbInlineMessage>

      </div>
    </template>

    <template #footerButtonLeft>
      <GbButton
        type="button"
        gbTheme="grayOutlineSecondary"
        label="Cancel"
        :disabled="isSubmitting"
        @click="cancel"
      />
    </template>

    <template #footerButtonRight>
      <GbButton
        type="button"
        gbTheme="bluePrimary"
        :disabled="isSubmitting"
        @click="submit"
      >
        <span v-if="isSubmitting" class="flex items-center">
          <ProgressSpinner class="mr-2 !h-5 !w-5 stroke-white" strokeWidth="4" />
          Saving...
        </span>
        <span v-else>{{ isEditMode ? 'Update' : 'Add' }}</span>
      </GbButton>
    </template>
  </GbDialog>
</template>
